import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, MapPin, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { track } from "../lib/analytics";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { LeadForm } from "../components/LeadForm";
import { PedagogyCycle } from "../components/PedagogyCycle";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";

const ATELIER_CATEGORIES = ["Çocuk", "Genç", "Ebeveyn", "Öğretmen", "Lider", "Yetişkin", "Kitap & düşünce buluşmaları"];

export default function ProgramDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [events, setEvents] = useState([]);
  const [notFound, setNotFound] = useState(false);

  const isAtelier = slug === "bilgece-atolye";
  const isPedagogy = slug === "bilgelik-pedagojisi";
  const leadSource = isAtelier ? "atelier_pre_registration" : "individual_program";

  useEffect(() => {
    setP(null); setNotFound(false);
    api.get(`/programs/${slug}`)
      .then(({ data }) => { setP(data); track("program_view", { program: data.name }); })
      .catch(() => setNotFound(true));
    if (slug === "bilgece-atolye") {
      api.get("/events").then(({ data }) => setEvents(data)).catch(() => {});
    }
  }, [slug]);

  if (notFound) return (
    <div className="pt-40 pb-32 text-center">
      <h1 className="font-serif text-4xl text-brand-green">Program bulunamadı</h1>
      <Link to="/programlar" className="text-brand-gold mt-4 inline-block link-underline">Tüm programlar</Link>
    </div>
  );
  if (!p) return <div className="pt-40 pb-40 text-center text-brand-text/50">Yükleniyor…</div>;

  const jsonLd = { "@context": "https://schema.org", "@type": "Course", name: p.name, description: p.tagline, provider: { "@type": "Organization", name: "Bilgece" } };

  return (
    <>
      <Seo title={p.name} description={p.tagline} image={p.cover_image} jsonLd={jsonLd} />

      {/* HERO */}
      <section className="relative pt-36 pb-24 grain overflow-hidden">
        <div className="absolute inset-0">
          <img src={p.cover_image} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-brand-green/85" />
        </div>
        <div className="relative container-narrow px-6 lg:px-10 text-brand-offwhite">
          <p className="text-xs uppercase tracking-[0.25em] text-brand-gold-light font-semibold mb-4">{p.audience}</p>
          <h1 className="font-serif text-4xl md:text-6xl font-light max-w-3xl">{p.name}</h1>
          <p className="text-brand-offwhite/85 text-lg mt-6 max-w-2xl leading-relaxed">{p.hero_subtitle}</p>
          <a href="#basvuru" onClick={() => track("program_cta_click", { program: p.name })} data-testid="program-hero-cta"
            className="inline-flex items-center gap-2 mt-9 bg-brand-gold text-brand-offwhite hover:bg-brand-offwhite hover:text-brand-green transition-colors px-8 py-4 text-sm font-semibold rounded-sm">
            {p.cta_label} <ArrowRight size={18} />
          </a>
        </div>
      </section>

      {/* WHY */}
      <section className="section-pad bg-brand-offwhite">
        <div className="container-narrow grid lg:grid-cols-3 gap-12">
          <Reveal><h2 className="font-serif text-3xl md:text-4xl text-brand-green">Bu program neden var?</h2></Reveal>
          <Reveal delay={0.1} className="lg:col-span-2"><p className="text-brand-text/75 text-lg leading-relaxed">{p.why}</p></Reveal>
        </div>
      </section>

      {isPedagogy && (
        <section className="section-pad bg-brand-cream">
          <div className="container-narrow"><PedagogyCycle /></div>
        </section>
      )}

      {/* FOR WHOM + GAINS */}
      <section className="section-pad bg-brand-cream">
        <div className="container-narrow grid md:grid-cols-2 gap-14">
          <Reveal>
            <h3 className="font-serif text-3xl text-brand-green mb-6">Kimler için?</h3>
            <ul className="space-y-3">
              {p.for_whom.map((f, i) => (
                <li key={i} className="flex gap-3 text-brand-text/80"><Check className="text-brand-gold shrink-0 mt-1" size={18} /><span>{f}</span></li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <h3 className="font-serif text-3xl text-brand-green mb-6">Ne kazandırır?</h3>
            <ul className="space-y-3">
              {p.gains.map((g, i) => (
                <li key={i} className="flex gap-3 text-brand-text/80"><Check className="text-brand-gold shrink-0 mt-1" size={18} /><span>{g}</span></li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section-pad bg-brand-offwhite">
        <div className="container-narrow">
          <Reveal><h3 className="font-serif text-3xl md:text-4xl text-brand-green mb-12">Nasıl işler?</h3></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {p.how_it_works.map((h, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="h-full bg-brand-cream border border-brand-gold-light/40 rounded-sm p-7">
                  <span className="font-serif text-4xl text-brand-gold">{String(i + 1).padStart(2, "0")}</span>
                  <p className="text-brand-text/80 text-sm mt-4 leading-relaxed">{h}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MODULES + APPROACH */}
      <section className="section-pad bg-brand-green text-brand-offwhite grain relative overflow-hidden">
        <div className="container-narrow grid lg:grid-cols-2 gap-14">
          <Reveal>
            <h3 className="font-serif text-3xl md:text-4xl mb-6">İçerik &amp; Modüller</h3>
            <div className="flex flex-wrap gap-3">
              {p.modules.map((m, i) => (
                <span key={i} className="bg-brand-green2 border border-brand-offwhite/15 px-4 py-2 rounded-full text-sm">{m}</span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h3 className="font-serif text-3xl md:text-4xl mb-6">Yaklaşım</h3>
            <p className="text-brand-offwhite/80 text-lg leading-relaxed italic font-serif">{p.approach}</p>
          </Reveal>
        </div>
      </section>

      {/* ATELIER-SPECIFIC */}
      {isAtelier && (
        <>
          <section className="section-pad bg-brand-offwhite">
            <div className="container-narrow">
              <Reveal><h3 className="font-serif text-3xl md:text-4xl text-brand-green mb-10">Atölye kategorileri</h3></Reveal>
              <div className="flex flex-wrap gap-3">
                {ATELIER_CATEGORIES.map((c) => (
                  <span key={c} className="bg-brand-cream border border-brand-gold-light/50 text-brand-green px-5 py-2.5 rounded-full text-sm font-medium">{c}</span>
                ))}
              </div>
            </div>
          </section>

          <section className="section-pad bg-brand-cream">
            <div className="container-narrow">
              <Reveal><h3 className="font-serif text-3xl md:text-4xl text-brand-green mb-10">Yaklaşan programlar</h3></Reveal>
              <div className="grid md:grid-cols-2 gap-6">
                {events.length === 0 && <p className="text-brand-text/60">Yakında yeni programlar duyurulacak.</p>}
                {events.map((e) => (
                  <Link to={`/etkinlikler/${e.slug}`} key={e.id} data-testid={`event-${e.id}`} className="block bg-brand-offwhite border border-brand-gold-light/40 rounded-sm p-7 hover:border-brand-gold transition-colors">
                    <span className="text-xs uppercase tracking-widest text-brand-gold font-semibold">{e.category}</span>
                    <h4 className="font-serif text-2xl text-brand-green mt-2">{e.title}</h4>
                    <p className="text-brand-text/70 text-sm mt-2">{e.description}</p>
                    <div className="flex items-center gap-2 mt-4 text-sm text-brand-text/60"><MapPin size={15} className="text-brand-gold" /> {(e.start || e.date || "").replace("T", " ")} · {e.location}</div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          <section className="section-pad bg-brand-offwhite">
            <div className="container-narrow grid lg:grid-cols-2 gap-14 items-center">
              <Reveal>
                <h3 className="font-serif text-3xl md:text-4xl text-brand-green mb-4">Mekân &amp; Lokasyon</h3>
                <p className="text-brand-text/75 leading-relaxed mb-6">Bilgece Atölye, birlikte düşünmenin ve deneyimlemenin mekânı. İlk lokasyonumuz Kadıköy'de.</p>
                <div className="bg-brand-cream border border-brand-gold-light/40 rounded-sm p-7">
                  <div className="flex gap-3 text-brand-text/80"><MapPin className="text-brand-gold shrink-0 mt-1" size={20} />
                    <address className="not-italic leading-relaxed">
                      Sahrayıcedit Mahallesi, İnönü Caddesi<br />Aydoğanlar İş Merkezi<br />No:12 Kat:5 Daire:13<br />Kadıköy / İstanbul
                    </address>
                  </div>
                  <a href="https://www.google.com/maps/search/?api=1&query=Sahray%C4%B1cedit+%C4%B0n%C3%B6n%C3%BC+Caddesi+Kad%C4%B1k%C3%B6y" target="_blank" rel="noopener noreferrer"
                    onClick={() => track("outbound_click", { to: "google_maps" })} data-testid="atelier-maps-link"
                    className="inline-flex items-center gap-2 mt-5 text-brand-green font-semibold text-sm link-underline">
                    Google Maps'te aç <ArrowRight size={15} />
                  </a>
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="rounded-sm overflow-hidden border border-brand-gold-light/40">
                  <iframe title="Bilgece Atölye Konum" width="100%" height="360" style={{ border: 0 }} loading="lazy"
                    src="https://maps.google.com/maps?q=Sahray%C4%B1cedit%20%C4%B0n%C3%B6n%C3%BC%20Caddesi%20Kad%C4%B1k%C3%B6y&t=&z=15&ie=UTF8&iwloc=&output=embed" />
                </div>
              </Reveal>
            </div>
          </section>
        </>
      )}

      {/* FAQ */}
      {p.faqs?.length > 0 && (
        <section className="section-pad bg-brand-cream">
          <div className="container-narrow max-w-3xl">
            <Reveal><h3 className="font-serif text-3xl md:text-4xl text-brand-green mb-8">Sık sorulan sorular</h3></Reveal>
            <Accordion type="single" collapsible className="w-full">
              {p.faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-${i}`}>
                  <AccordionTrigger className="text-left font-serif text-xl text-brand-green">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-brand-text/75 text-base leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* APPLICATION */}
      <section id="basvuru" className="section-pad bg-brand-offwhite scroll-mt-24">
        <div className="container-narrow max-w-3xl">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-brand-gold font-semibold mb-3">{isAtelier ? "Ön Kayıt" : "Bilgi Talebi"}</p>
            <h3 className="font-serif text-3xl md:text-5xl text-brand-green mb-3">{isAtelier ? "Ön kayıt oluştur" : "Bilgi talep et"}</h3>
            <p className="text-brand-text/70 mb-8">Formu doldur, ekibimiz en kısa sürede seninle iletişime geçsin.</p>
          </Reveal>
          <div className="bg-brand-cream border border-brand-gold-light/40 rounded-sm p-8 md:p-10">
            <LeadForm
              testid="program-lead-form"
              source={leadSource}
              program={p.name}
              fields={isAtelier ? ["name", "email", "phone", "category", "message"] : ["name", "email", "phone", "message"]}
              categories={isAtelier ? ATELIER_CATEGORIES : null}
              submitLabel={isAtelier ? "Ön Kaydımı Oluştur" : "Bilgi Talebi Gönder"}
              onStarted={() => track(isAtelier ? "atelier_pre_registration_started" : "program_cta_click", { program: p.name })}
              onCompleted={() => isAtelier && track("atelier_pre_registration_completed", { program: p.name })}
            />
          </div>
        </div>
      </section>
    </>
  );
}
